import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Tech interview best practice that fails in life",
  "description": "Tech interviews have a magic answer: The hashmap. And it's not always best on the job.",
  "published": "2021-01-22T00:00:00.000Z",
  "categories": "Technical, Computer Science",
  "hero": "./img/screenshot-1611336874052.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Tech interviews have a magic answer: The hashmap.`}</p>
    <p><em parentName="p">{`"Great solution, how would you optimize it?"`}</em></p>
    <p>{`Hashmap!`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media1.giphy.com/media/26tk0cG50yIswFlPq/giphy-loop.mp4?cid=7cf28b0fx8l8jkbm1tg1cvwljk9glrq9gojqk9t77grwc1p0&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`You get the job and start to work. Your vast interview prep (and half of college) prepared you to answer every question with a hashmap. Sometimes a graph.`}</p>
    <p>{`And in life that instinct is wrong.`}</p>
    <p>{`I was thinking about this while writing `}<a parentName="p" {...{
        "href": "https://serverlesshandbook.dev/serverless-performance/"
      }}>{`the performance chapter of Serverless Handbook`}</a>{`. What belief about performance shoots engineers in the foot? 🤔`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`“Hashmap” is always the answer in an interview. But in real life it’s not. `}<a parentName="p" {...{
          "href": "https://t.co/zCK9lDKeS5"
        }}>{`pic.twitter.com/zCK9lDKeS5`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1351230210109276161"
      }}>{`January 18, 2021`}</a></blockquote>
    <h2 {...{
      "id": "a-note-on-big-o"
    }}>{`A note on big-O`}</h2>
    <p>{`We're going to use `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Big_O_notation"
      }}>{`Big-O`}</a>{` to describe algorithm complexity. A popularly abused notation in engineering circles.`}</p>
    <p>{`Formally Big-O talks about function growth at a particular type of limit. Other useful notations include `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Big_O_notation#Family_of_Bachmann%E2%80%93Landau_notations"
      }}>{`Big Theta and Big Omega`}</a>{` about average and lower-bound performance respectively.`}</p>
    <p>{`Proper analysis can be confusing. We'll use the abused version:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`O(1)`}</inlineCode>{` means a single operation lookup (hashmap, certain set operations, reading a variable)`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`O(logn)`}</inlineCode>{` means you divide remaining steps with each iteration (divide and conquer algorithms)`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`O(n)`}</inlineCode>{` means you iterate the full input (iteration algorithms)`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`O(n^2)`}</inlineCode>{` means you iterate the full input for every entry (exponent counts nested loops)`}</li>
    </ul>
    <p>{`We ignore constant factors – `}<inlineCode parentName="p">{`O(2n) == O(n)`}</inlineCode>{` – and that's where interview best practices break down.`}</p>
    <h2 {...{
      "id": "an-easy-phone-screen-question"
    }}>{`An easy phone screen question`}</h2>
    <p>{`At `}<a parentName="p" {...{
        "href": "https://asktia.com"
      }}>{`Tia`}</a>{` we ask senior fullstack candidates this question in the phone screen:`}</p>
    <blockquote>
      <p parentName="blockquote">{`Given a newspaper article and a sentence, write a function that answers "Can you create the sentence out of words in the article?". Use any language. Words delimited by spaces.`}</p>
    </blockquote>
    <p>{`Long for a phone screen, but we pay well 😛`}</p>
    <p>{`You look at this problem and hashmap is the obvious answer. Count words in the article, count words in the sentence, compare.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`def countWords(list):
    counts = {}
    for word in list:
        if counts[word]:
            counts[word] += 1
        else:
            counts[word] = 1
    return counts

def canBuildSentence(article, sentence):
    haystack = countWords(article.split(' '))
    needle = countWords(sentence.split(' '))

    for word in needle.keys():
        haystack[word] -= needle[word]
        if haystack[word] < 0:
            return False

    return True
`}</code></pre>
    <p>{`A roughly `}<inlineCode parentName="p">{`O(n)`}</inlineCode>{` algorithm, fantastic.`}</p>
    <p>{`You iterate the article to build a hashmap – `}<inlineCode parentName="p">{`O(n)`}</inlineCode>{`. Each word lookup is `}<inlineCode parentName="p">{`O(1)`}</inlineCode>{` because hashmap. Iterate the sentence, another `}<inlineCode parentName="p">{`O(n)`}</inlineCode>{`. Then you iterate the keys from your sentence and do `}<inlineCode parentName="p">{`O(1)`}</inlineCode>{` lookups.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media1.giphy.com/media/Joybb4YVIpf4NE9x8k/giphy-loop.mp4?cid=7cf28b0fr2mkpm7l2q32c8o1xtoiuq06soov9kaakdqwcxbz&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <h3 {...{
      "id": "can-you-improve-performance"
    }}>{`Can you improve performance?`}</h3>
    <p>{`This algorithm is hard on memory. We like to ignore memory constraints in 2021 because memory is cheap.`}</p>
    <p>{`But you could get ridiculously large inputs.`}</p>
    <p>{`The `}<a parentName="p" {...{
        "href": "http://www.plainenglish.co.uk/campaigning/examples/long-sentences.html"
      }}>{`longest sentence in English literature`}</a>{` contains 13,955 words. The `}<a parentName="p" {...{
        "href": "https://www.newyorker.com/magazine/2021/01/04/the-plague-year"
      }}>{`New Yorker's Plague Year article`}</a>{` is around 30,000 words long. Took the entire magazine. Wikipedia's longest article has 634kB of text – about 33,000 words.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`The longest Wikipedia article is Joe Biden’s 2020 endorsements `}<a parentName="p" {...{
          "href": "https://t.co/P5uetnJ2Mu"
        }}>{`pic.twitter.com/P5uetnJ2Mu`}</a></p>{`— Kiran 🗳 (@MichiganKiran) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/MichiganKiran/status/1302682068665667585"
      }}>{`September 6, 2020`}</a></blockquote>
    <p>{`Curveballs are fun!`}</p>
    <h3 {...{
      "id": "constants-matter"
    }}>{`Constants matter`}</h3>
    <p>{`You're not gonna run out of memory. All that text barely makes a dent next to your execution environment. But do you need to build `}<em parentName="p">{`two`}</em>{` hashmaps?`}</p>
    <p>{`Serverless environments pay per millisecond. Can you shave off milliseconds?`}</p>
    <p>{`🤔`}</p>
    <p>{`To check the longest sentence against Joe Biden's endorsements, takes `}<inlineCode parentName="p">{`33000 + 13955 + 13955 = 60,910`}</inlineCode>{` operations. Say each one takes 1/100th of a millisecond.`}</p>
    <p>{`Your algorithm runs in 609 milliseconds. That's slow, mate. User-noticeable latency.`}</p>
    <p>{`You can remove a hashmap:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`def canBuildSentence(article, sentence):
    haystack = countWords(article.split(' '))

    for word in sentence.split(' '):
        haystack[word] -= 1
        if haystack[word] < 0:
            return False

    return True
`}</code></pre>
    <p>{`Now you're down to `}<inlineCode parentName="p">{`33000 + 13955 = 46,955`}</inlineCode>{` operations. 23% better performance, 23% lower cost, `}<a parentName="p" {...{
        "href": "https://www.cloudflare.com/learning/performance/more/website-performance-conversion-rates/"
      }}>{`better conversions, more sales`}</a>{`.`}</p>
    <p>{`Same `}<inlineCode parentName="p">{`O(n)`}</inlineCode>{` complexity. 🤯`}</p>
    <h2 {...{
      "id": "when-hashmaps-arent-the-answer"
    }}>{`When hashmaps aren't the answer`}</h2>
    <p>{`What if you know inputs are small?`}</p>
    <p>{`A sentence is never longer than 5 `}<em parentName="p">{`unique`}</em>{` words and an article never shorter than 5,000. Do you change your approach?`}</p>
    <p>{`You don't have to. The same algorithm works – 5005 operations.`}</p>
    <p>{`Or you can leverage `}<a parentName="p" {...{
        "href": "https://en.wiktionary.org/wiki/Wiktionary:Frequency_lists"
      }}>{`English word frequency`}</a>{`. The top 1000 words cover 85.5% of uses, 10,000 gets you to 97.2%.`}</p>
    <p>{`There's an 85.5% chance you'll find those 5 words in the first 1000 words of your article. Hmmmm 🤔`}</p>
    <p>{`A search algorithm might work better:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`def wordInHaystack(word, haystack):
    for needle in haystack:
        if needle == word:
            return True
    return False

def canBuildSentence(article, sentence):
    haystack = article.split(' ')

    for word in sentence.split(' '):
        if !wordInHaystack(word, haystack):
            return False

    return True
`}</code></pre>
    <p>{`Iterate the article looking for a word. 85.5% of the time, you look at fewer than 1000 words. 15.5% of the time, you look at 5000.`}</p>
    <p>{`Your algorithm has an `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Expected_value"
      }}>{`expected operation count`}</a>{` of `}<inlineCode parentName="p">{`E(5\\*(1000\\*0.855 + 5000\\*0.155)) = 8150`}</inlineCode>{`. Which is worse than 5005.`}</p>
    <p>{`But it's important to check. It works for 2-word sentences and shorter 😇`}</p>
    <p>{`Or when you get lucky.`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>
    <p>{`PS: you can sort both inputs, consume arrays, and compare that way. It's `}<inlineCode parentName="p">{`O(nlogn)`}</inlineCode>{` complexity but zero overhead and suitable for streaming/distributed solutions.`}</p>
    <p>{`PPS: a great discussion on StackOverflow comparing `}<a parentName="p" {...{
        "href": "https://stackoverflow.com/questions/29218440/when-merge-sort-is-preferred-over-quick-sort"
      }}>{`Quicksort and Mergesort`}</a>{` – both typical O(nlogn) algorithms – and how they behave differently in real world applications. Fun to skim ✌️`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      